import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styled from "styled-components";
import { Input, Spin } from "antd";
import { CaretLeft, FileText, MagnifyingGlass } from "@phosphor-icons/react";
import debounce from "lodash.debounce";
import { searchDocumentsAPI } from "../../service/search";
import MobileDocumentViewer from "../../components/MobileDocumentViewer/MobileDocumentViewer";
import {
  LoadMoreButton,
  ResultMeta,
  ResultRow,
  ResultTitle,
  SearchStatus,
} from "../Search/SearchStyled";
import {
  border,
  radius,
  spacing,
  surface,
  text,
  typography,
} from "../../theme/tokens";
import {
  DetailBackButton,
  DetailHeader,
  DetailSubtitle,
  DetailTitle,
  DetailTitleBlock,
} from "../Inbox/MobileInboxStyled";

/**
 * Minimal mobile matter workspace (Mobile DMS v1, Phase 4).
 *
 * Replaces the desktop `MatterDetails` (folder tree + Apryse + AI
 * rail) below the mobile breakpoint. Scope is deliberately v1-small:
 * a flat recent-first document list with in-matter search, and a
 * full-screen lightweight viewer.
 *
 * URL contract:
 *   /matter/:id           → document list
 *   /matter/:id?doc=<id>  → viewer over that document (used by the
 *                           Search page's document results)
 *   /matter/:id/D<pk>     → desktop-style doc deep link (briefing
 *                           citations, shared links) — the splat is
 *                           mapped onto the same viewer.
 */

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${surface.surface};
`;

const MatterHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[3]} ${spacing[2.5]};
  border-bottom: 1px solid ${border.light};
  flex-shrink: 0;
`;

const MatterName = styled.h1`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  word-break: break-word;
`;

const MatterDocCount = styled.div`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  font-variant-numeric: tabular-nums;
`;

const DocListArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const ViewerScreen = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${surface.surface};
`;

const ViewerBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-top-left-radius: ${radius.lg};
  overflow: hidden;
`;

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 350;

const MobileMatterView = () => {
  const { id: matterId, "*": splat } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState("");
  const [docs, setDocs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("loading"); // loading | ready | failed
  const [loadingMore, setLoadingMore] = useState(false);
  const requestSeqRef = useRef(0);

  // Selected document: `?doc=` wins, then a desktop-style `/D<pk>`
  // splat segment (briefing citation links).
  const splatDocId = useMemo(() => {
    const first = (splat || "").split("/").filter(Boolean)[0];
    return first && /^D[\w-]+$/i.test(first) ? first : null;
  }, [splat]);
  const selectedDocId = searchParams.get("doc") || splatDocId;

  const fetchDocs = useCallback(
    async (term, targetPage = 1) => {
      const seq = ++requestSeqRef.current;
      if (targetPage === 1) setStatus("loading");
      else setLoadingMore(true);
      try {
        const response = await searchDocumentsAPI({
          query: term.trim(),
          matterId,
          page: targetPage,
          pageSize: PAGE_SIZE,
        });
        if (seq !== requestSeqRef.current) return;
        const results = response?.data?.results || [];
        setDocs((prev) =>
          targetPage === 1 ? results : [...prev, ...results]
        );
        setCount(response?.data?.count || results.length);
        setPage(targetPage);
        setStatus("ready");
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        console.error("Failed to load matter documents:", err);
        setStatus("failed");
      } finally {
        if (seq === requestSeqRef.current) setLoadingMore(false);
      }
    },
    [matterId]
  );

  useEffect(() => {
    fetchDocs("");
  }, [fetchDocs]);

  const debouncedFetch = useMemo(
    () => debounce((term) => fetchDocs(term, 1), DEBOUNCE_MS),
    [fetchDocs]
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const handleQueryChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    debouncedFetch(value);
  };

  const handleOpenDocument = (doc) => {
    setSearchParams({ doc: String(doc.id) });
  };

  const handleCloseViewer = () => {
    if (searchParams.get("doc")) {
      setSearchParams({});
      return;
    }
    // Splat-based deep link: strip the doc segment.
    navigate(`/matter/${matterId}`, { replace: true });
  };

  const matterName = location.state?.matterName || "Matter";

  const selectedDoc = useMemo(() => {
    if (!selectedDocId) return null;
    return (
      docs.find((doc) => String(doc.id) === String(selectedDocId)) || null
    );
  }, [docs, selectedDocId]);

  if (selectedDocId) {
    const documentName =
      selectedDoc?.name || location.state?.documentName || "Document";
    return (
      <ViewerScreen>
        <DetailHeader>
          <DetailBackButton
            type="button"
            onClick={handleCloseViewer}
            aria-label="Back to document list"
          >
            <CaretLeft size={20} weight="bold" />
          </DetailBackButton>
          <DetailTitleBlock>
            <DetailTitle>{documentName}</DetailTitle>
            <DetailSubtitle>{matterName}</DetailSubtitle>
          </DetailTitleBlock>
        </DetailHeader>
        <ViewerBody>
          <MobileDocumentViewer
            documentId={selectedDocId}
            fileType={
              selectedDoc?.file_type ||
              location.state?.documentFileType ||
              ""
            }
            documentName={documentName}
          />
        </ViewerBody>
      </ViewerScreen>
    );
  }

  return (
    <ListWrapper>
      <MatterHeader>
        <MatterName>{matterName}</MatterName>
        {status === "ready" && (
          <MatterDocCount>
            {count.toLocaleString()} document{count === 1 ? "" : "s"}
          </MatterDocCount>
        )}
        <Input
          allowClear
          value={query}
          onChange={handleQueryChange}
          placeholder="Search in this matter…"
          prefix={<MagnifyingGlass size={16} color="#7688A3" />}
          aria-label="Search documents in this matter"
        />
      </MatterHeader>

      <DocListArea>
        {status === "loading" && (
          <SearchStatus role="status">
            <Spin />
            Loading documents…
          </SearchStatus>
        )}

        {status === "failed" && (
          <SearchStatus role="alert">
            Couldn&apos;t load this matter&apos;s documents — check your
            connection and try again.
          </SearchStatus>
        )}

        {status === "ready" && docs.length === 0 && (
          <SearchStatus role="status">
            <FileText size={32} weight="duotone" color="#98A6BA" />
            {query.trim()
              ? `No documents matched “${query.trim()}”.`
              : "No documents in this matter yet."}
          </SearchStatus>
        )}

        {status === "ready" &&
          docs.map((doc) => (
            <ResultRow
              key={doc.id}
              type="button"
              onClick={() => handleOpenDocument(doc)}
            >
              <ResultTitle>{doc.name || "Untitled document"}</ResultTitle>
              <ResultMeta>
                {[doc.category, doc.file_type?.toUpperCase()]
                  .filter(Boolean)
                  .join(" · ") || "Document"}
              </ResultMeta>
            </ResultRow>
          ))}

        {status === "ready" && docs.length < count && (
          <LoadMoreButton
            type="button"
            disabled={loadingMore}
            onClick={() => fetchDocs(query, page + 1)}
          >
            {loadingMore
              ? "Loading…"
              : `Load more (${docs.length} of ${count})`}
          </LoadMoreButton>
        )}
      </DocListArea>
    </ListWrapper>
  );
};

export default MobileMatterView;
